<template>
  <div class="mx-auto flex w-full max-w-[864px] flex-col justify-center overflow-y-auto py-7 text-center">
    <Slider
      v-if="hasSizes"
      ref="slider"
      :deselect-item="deselectItem"
      :items="sizes"
      @update:model-value="(val) => selectDimension(val)"
    >
      <template #content="{ item }">
        <div class="relative">
          <div v-if="item.type == 'custom'" class="absolute -top-1 right-0">
            <DropdownActions
              trigger-icon-prefix="bold/DotsThreeVertical"
              :actions="[
                {
                  label: t('delete'),
                  destructive: true,
                  handler: () => deleteDimension(item),
                  value: 'delete',
                },
              ]"
            />
          </div>
          <Icon class="mx-auto mb-2 max-w-[32px]" src="light/cube_transparent" :size="ComponentSize.xl4" />
          <p class="text-body-default-heavy">
            {{ item.name }}
          </p>
          <p class="text-body-default">{{ item.length }} x {{ item.width }} x {{ item.height || 0 }}</p>
        </div>
      </template>
    </Slider>
    <Slider v-else :items="Array.from({ length: 5 })" :deselect-item="false">
      <template #item>
        <div class="flex flex-col items-center gap-2">
          <Bone class="shadow-inset-00-01 mx-auto h-28 min-w-45 rounded-lg" type="light" />
        </div>
      </template>
    </Slider>

    <div class="mx-10 mt-5 mb-3 flex items-center gap-4">
      <InputValueXL
        v-model="length"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('length')"
        data-testid="length-input"
        @update:model-value="deselectItem = !deselectItem"
      />
      <Icon class="mt-6" src="X" :size="ComponentSize.lg" />
      <InputValueXL
        v-model="width"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('width')"
        data-testid="width-input"
        @update:model-value="deselectItem = !deselectItem"
      />
      <Icon class="mt-6" src="X" :size="ComponentSize.lg" />
      <InputValueXL
        v-model="height"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('height')"
        data-testid="height-input"
        @update:model-value="deselectItem = !deselectItem"
      />
    </div>
    <Button
      class="mx-auto mb-5 w-full max-w-max px-1"
      type="button"
      :disabled="!length || !width"
      :variant="ButtonVariant.Ghost"
      @click="saveSizeOpen = true"
      >{{ t("save_as_standard") }}</Button
    >
    <template v-if="length > 0 && width > 0">
      <Divider class="my-5" />
      <InputValueXL
        v-model="quantity"
        :suffix="t('pieces')"
        required
        min="1"
        :label="t('qty')"
        data-testid="qty-input"
        class="mx-auto max-w-64"
      />
    </template>
  </div>
  <Modal ref="modal" v-model:open="saveSizeOpen" size="fixed" width="342px">
    <div class="flex flex-col gap-4">
      <InputText v-model="customSizeName" :label="t('name_new_size')" :placeholder="t('size_name')" />
      <p class="text-title-body mx-auto flex items-center gap-2">
        L:{{ length }}cm
        <Icon src="X" :size="ComponentSize.sm" />
        B:{{ width }}cm
        <Icon src="X" :size="ComponentSize.sm" />
        H:{{ height }}cm
      </p>
    </div>
    <template #footer>
      <ModalFooter
        :confirm-text="`${t('save')} ${t('size').toLowerCase()}`"
        :disabled="!customSizeName"
        @cancel="saveSizeOpen = false"
        @confirm="saveSize"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
  interface Size {
    name: string;
    length: number;
    width: number;
    height: number;
  }

  const { t } = useI18n();

  const customSizeName = ref("");

  const saveSizeOpen = ref(false);

  const deselectItem = ref(false);

  const slider = useTemplateRef("slider");

  const sizes = useState("newconsignment_dimensions", () => []) as Ref<Size[]>;

  const hasSizes = computed(() => sizes.value.length > 0);

  const fetchDimensions = async () => {
    const startTime = Date.now();
    const waitTime = 350;
    const response = await homeFetch<Size[]>("dimensions?include_standard=false");

    // Calculate time elapsed
    const timeElapsed = Date.now() - startTime;

    // If less than waitTime has passed, wait for the remaining time
    if (timeElapsed < waitTime) {
      await new Promise((resolve) => setTimeout(resolve, waitTime - timeElapsed));
    }

    if (response?.data) sizes.value = response.data;
    return response;
  };

  const saveSize = () => {
    const size = {
      length: length.value,
      width: width.value,
      height: height.value || 0,
      name: customSizeName.value,
    };
    homeFetch("dimensions", {
      method: "POST",
      body: size,
    })
      .then((res) => {
        const newId = res?.data?.id;
        fetchDimensions().then(() => {
          const dimensionIndex = sizes.value?.findIndex((s) => s.id === newId);

          slider.value?.updateSelection(res?.data, dimensionIndex, true);
        });
      })
      .then(() => {
        toastAdd({
          text: t("dimension_saved"),
        });
        saveSizeOpen.value = false;
        customSizeName.value = "";
      });
  };

  const selectDimension = (size: Size) => {
    length.value = size?.length;
    width.value = size?.width || 10;
    height.value = size?.height || 0;
  };

  const deleteDimension = (size: Size) => {
    homeFetch(`dimensions/${size.id}`, {
      method: "DELETE",
    }).then(() => {
      sizes.value = sizes.value.filter((s: Size) => s.id !== size.id);
    });
  };

  const { value: width } = useField<number>(() => "width", undefined, {
    syncVModel: true,
  });

  const { value: length } = useField<number>(() => "length", undefined, {
    syncVModel: true,
  });

  const { value: height } = useField<number>(() => "height", undefined, {
    syncVModel: true,
  });

  const { value: quantity } = useField<number>(() => "quantity", undefined, {
    syncVModel: true,
  });

  onBeforeMount(() => fetchDimensions());
</script>
